import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getArnulfoDashboard,
  updateArnulfoContainer,
  createArnulfoPayment,
  deleteArnulfoPayment,
} from '../lib/api';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  DollarSign, Package, CheckCircle, Loader2, ShieldAlert,
  ChevronRight, ChevronDown, Wallet, Plus, Trash2,
} from 'lucide-react';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency', currency: 'MXN',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(amount || 0);

const PAYMENT_METHODS = ['TRANSFERENCIA', 'EFECTIVO', 'DEPOSITO', 'CHEQUE'];

function KPICard({ icon: Icon, label, value, color }) {
  const colorClasses = {
    blue: 'from-blue-500/20 to-blue-600/5 text-blue-400',
    green: 'from-emerald-500/20 to-emerald-600/5 text-emerald-400',
    amber: 'from-amber-500/20 to-amber-600/5 text-amber-400',
    red: 'from-red-500/20 to-red-600/5 text-red-400',
  };
  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="mt-4">
        <p className="text-3xl font-heading text-foreground">{value}</p>
        <p className="text-sm text-muted-foreground mt-1">{label}</p>
      </div>
    </div>
  );
}

// Editable "Monto Acordado" for a container (the per-cargo breakdown)
function AcordadoRow({ container, canEdit, onSaved }) {
  const [acordado, setAcordado] = useState(container.monto_acordado ?? '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (String(acordado) === String(container.monto_acordado ?? '')) return;
    setSaving(true);
    try {
      await updateArnulfoContainer(container.container_number, { monto_acordado: acordado });
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr
      className={`border-b border-border/40 text-sm ${container.counts ? '' : 'opacity-60'}`}
      data-testid={`arnulfo-container-row-${container.container_number}`}
    >
      <td className="py-2 px-4 font-mono">
        {container.container_number}
        {!container.counts && (
          <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground">
            no cuenta (sin entregar)
          </span>
        )}
        {saving && <Loader2 className="inline w-3 h-3 ml-2 animate-spin" />}
      </td>
      <td className="py-2 px-4">
        {canEdit ? (
          <Input
            type="number"
            className="h-8 w-36"
            value={acordado}
            placeholder={container.monto_acordado_is_default ? '60000' : '0.00'}
            onChange={(e) => setAcordado(e.target.value)}
            onBlur={save}
            data-testid={`acordado-${container.container_number}`}
          />
        ) : (
          <span>{container.monto_acordado != null ? formatCurrency(container.monto_acordado) : '—'}</span>
        )}
      </td>
    </tr>
  );
}

export default function ArnulfoPage() {
  const { user, hasFeature } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({
    amount: '', payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'TRANSFERENCIA', reference: '',
  });
  const [savingPay, setSavingPay] = useState(false);
  const isInternal = !!(user?.is_master_admin || user?.is_super_admin);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getArnulfoDashboard();
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 403) toast.error('No tienes permiso para ver Arnulfo');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (mbl) => setExpanded((p) => ({ ...p, [mbl]: !p[mbl] }));

  const submitPayment = async () => {
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    setSavingPay(true);
    try {
      await createArnulfoPayment(payForm);
      toast.success('Pago registrado');
      setPayOpen(false);
      setPayForm({
        amount: '', payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'TRANSFERENCIA', reference: '',
      });
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setSavingPay(false);
    }
  };

  const removePayment = async (id) => {
    try {
      await deleteArnulfoPayment(id);
      toast.success('Pago eliminado');
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Access: OFIMEX internal staff OR an ORG ADMIN with the feature enabled.
  if (!isInternal && !(user?.role === 'admin' && hasFeature('client_arnulfo'))) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-[60vh] text-center">
          <ShieldAlert size={64} className="text-red-400 mb-4" />
          <h1 className="text-2xl font-heading text-foreground mb-2">Acceso Restringido</h1>
          <p className="text-muted-foreground">No tienes permiso para acceder al módulo Arnulfo.</p>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const stats = data?.stats || {};
  const shipments = data?.shipments || [];
  const payments = data?.payments || [];
  const canEdit = !!data?.can_edit;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="arnulfo-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-3xl text-foreground flex items-center gap-3">
              <DollarSign className="text-amber-400" />
              Cuenta Arnulfo
            </h1>
            <p className="text-muted-foreground mt-1">
              Cargas que aplican Arnulfo y su monto a pagar. Cada carga cuenta solo cuando se entrega.
            </p>
          </div>
          {canEdit && (
            <Button onClick={() => setPayOpen(true)} data-testid="register-payment-btn">
              <Plus size={16} className="mr-2" /> Registrar Pago
            </Button>
          )}
        </div>

        {/* General account (single running ledger) */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPICard icon={Package} label="Cargas Arnulfo" value={stats.total || 0} color="blue" />
          <KPICard icon={DollarSign} label="Total a Pagar (entregadas)" value={formatCurrency(stats.total_acordado)} color="amber" />
          <KPICard icon={CheckCircle} label="Total Pagado" value={formatCurrency(stats.total_pagado)} color="green" />
          <KPICard icon={Wallet} label="Saldo Pendiente" value={formatCurrency(stats.saldo_pendiente)} color="red" />
        </div>

        {/* Breakdown: shipments -> containers with agreed amount */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="font-heading text-lg">Desglose de cargas</h2>
          </div>
          {shipments.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Package size={48} className="mx-auto mb-4 opacity-30" />
              <p>No hay cargas con Arnulfo activo</p>
              <p className="text-sm mt-1">Activa Arnulfo desde el detalle del embarque</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-border bg-secondary/30">
                    <th className="py-3 px-4 font-medium w-8"></th>
                    <th className="py-3 px-4 font-medium">MBL</th>
                    <th className="py-3 px-4 font-medium">Mercancía</th>
                    <th className="py-3 px-4 font-medium">Contenedores</th>
                    <th className="py-3 px-4 font-medium">Monto a Pagar</th>
                  </tr>
                </thead>
                <tbody>
                  {shipments.map((s) => (
                    <React.Fragment key={s.mbl}>
                      <tr
                        className="border-b border-border/50 hover:bg-secondary/20 transition-colors cursor-pointer"
                        onClick={() => toggleRow(s.mbl)}
                        data-testid={`arnulfo-shipment-row-${s.mbl}`}
                      >
                        <td className="py-3 px-4">
                          {expanded[s.mbl] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </td>
                        <td className="py-3 px-4">
                          <Link
                            to={`/shipments/${encodeURIComponent(s.mbl)}`}
                            className="font-mono text-sm text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {s.mbl}
                          </Link>
                        </td>
                        <td className="py-3 px-4 text-sm">{s.cargo_type || '—'}</td>
                        <td className="py-3 px-4 text-sm font-mono">{s.container_count}</td>
                        <td className="py-3 px-4 text-sm font-semibold text-amber-400">{formatCurrency(s.total_acordado)}</td>
                      </tr>
                      {expanded[s.mbl] && (
                        <tr className="bg-secondary/10">
                          <td colSpan={5} className="p-0">
                            <div className="px-6 py-4">
                              {s.containers.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-2">Sin contenedores.</p>
                              ) : (
                                <table className="w-full">
                                  <thead>
                                    <tr className="text-left text-xs text-muted-foreground border-b border-border/60">
                                      <th className="py-2 px-4 font-medium">Contenedor</th>
                                      <th className="py-2 px-4 font-medium">Monto Acordado</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {s.containers.map((c) => (
                                      <AcordadoRow
                                        key={c.container_number}
                                        container={c}
                                        canEdit={canEdit}
                                        onSaved={loadData}
                                      />
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Payments ledger */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="font-heading text-lg">Pagos registrados</h2>
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => setPayOpen(true)} data-testid="register-payment-btn-2">
                <Plus size={14} className="mr-1" /> Registrar Pago
              </Button>
            )}
          </div>
          {payments.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">Aún no hay pagos registrados.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-border bg-secondary/30">
                    <th className="py-3 px-4 font-medium">Fecha</th>
                    <th className="py-3 px-4 font-medium">Método</th>
                    <th className="py-3 px-4 font-medium">Referencia</th>
                    <th className="py-3 px-4 font-medium text-right">Monto</th>
                    {canEdit && <th className="py-3 px-4 font-medium w-10"></th>}
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-b border-border/40 text-sm" data-testid={`payment-row-${p.id}`}>
                      <td className="py-2 px-4">{(p.payment_date || '').slice(0, 10)}</td>
                      <td className="py-2 px-4">{p.payment_method}</td>
                      <td className="py-2 px-4 text-muted-foreground">{p.reference || '—'}</td>
                      <td className="py-2 px-4 text-right text-emerald-400 font-medium">{formatCurrency(p.amount)}</td>
                      {canEdit && (
                        <td className="py-2 px-4">
                          <button
                            onClick={() => removePayment(p.id)}
                            className="text-muted-foreground hover:text-red-400"
                            data-testid={`delete-payment-${p.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="text-sm font-semibold border-t border-border">
                    <td className="py-3 px-4" colSpan={3}>Total pagado</td>
                    <td className="py-3 px-4 text-right text-emerald-400">{formatCurrency(stats.total_pagado)}</td>
                    {canEdit && <td></td>}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Register payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>Registrar Pago</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground">Monto</label>
              <Input
                type="number" placeholder="0.00" value={payForm.amount}
                onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                data-testid="payment-amount"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Fecha de pago</label>
              <Input
                type="date" value={payForm.payment_date}
                onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })}
                data-testid="payment-date"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Método</label>
              <Select
                value={payForm.payment_method}
                onValueChange={(v) => setPayForm({ ...payForm, payment_method: v })}
              >
                <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Referencia (opcional)</label>
              <Input
                value={payForm.reference}
                onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })}
                data-testid="payment-reference"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancelar</Button>
            <Button onClick={submitPayment} disabled={savingPay} data-testid="payment-submit">
              {savingPay ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
